import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// WhatsApp e Instagram usam limite combinado (integrations), team_members para membros de equipe
export type ResourceType = 'agents' | 'leads' | 'whatsapp' | 'instagram' | 'messenger' | 'integrations' | 'team_members' | 'automations' | 'calendars';

interface ResourceLimitCheck {
  can_create: boolean;
  current: number;
  limit: number;
  plan: string;
}

export function useResourceLimits(workspaceId: string | null) {
  const checkCanConnectIntegration = useCallback(async (): Promise<ResourceLimitCheck> => {
    if (!workspaceId) {
      return { can_create: false, current: 0, limit: 0, plan: 'inicial' };
    }

    try {
      const { data, error } = await supabase.rpc('check_integration_connection_limit', {
        p_workspace_id: workspaceId,
      });

      if (error) {
        console.error('Error checking integration connection limit:', error);
        return { can_create: true, current: 0, limit: 999, plan: 'unknown' };
      }

      const result = data as { allowed?: boolean; current?: number; max?: number; plan?: string } | null;
      return {
        can_create: result?.allowed ?? true,
        current: result?.current ?? 0,
        limit: result?.max ?? 999,
        plan: result?.plan ?? 'unknown',
      };
    } catch (err) {
      console.error('Unexpected error checking integration connection limit:', err);
      return { can_create: true, current: 0, limit: 999, plan: 'unknown' };
    }
  }, [workspaceId]);

  const checkCanCreate = useCallback(async (resourceType: ResourceType): Promise<ResourceLimitCheck> => {
    if (!workspaceId) {
      return { can_create: false, current: 0, limit: 0, plan: 'inicial' };
    }

    try {
      const { data, error } = await supabase
        .rpc('check_resource_limit' as any, {
          p_workspace_id: workspaceId,
          p_resource_type: resourceType
        });

      if (error) {
        console.error('Error checking resource limit:', error);
        return { can_create: true, current: 0, limit: 999, plan: 'unknown' };
      }

      const result = data as unknown as ResourceLimitCheck;
      return result || { can_create: true, current: 0, limit: 999, plan: 'unknown' };
    } catch (err) {
      console.error('Unexpected error checking resource limit:', err);
      return { can_create: true, current: 0, limit: 999, plan: 'unknown' };
    }
  }, [workspaceId]);

  return { checkCanCreate, checkCanConnectIntegration };
}
